import { useState, type FormEvent, type ReactNode } from 'react';
import { ApiError } from '../api.js';
import { formatRetryDelay } from '../format.js';
import { useStore } from '../store/StoreContext.js';

/**
 * Fond de l'ecran d'acces : un fragment de carte d'etat-major. Graticule,
 * courbes de niveau, route tiretee. Purement decoratif, donc masque aux
 * lecteurs d'ecran et non interactif.
 */
function GateBackdrop(): ReactNode {
  return (
    <svg
      className="gate__chart"
      viewBox="0 0 1400 900"
      preserveAspectRatio="xMidYMid slice"
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <pattern id="gate-grid" width="70" height="70" patternUnits="userSpaceOnUse">
          <path d="M70 0H0V70" fill="none" stroke="currentColor" strokeWidth="1" opacity="0.12" />
        </pattern>
        <path
          id="gate-contour"
          d="M0-124C74-130 148-84 158-21 168 47 115 110 42 126-38 143-116 105-142 37-166-26-100-118 0-124Z"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.4"
          vectorEffect="non-scaling-stroke"
        />
        <radialGradient id="gate-glow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#e9a13b" stopOpacity="0.16" />
          <stop offset="100%" stopColor="#e9a13b" stopOpacity="0" />
        </radialGradient>
      </defs>

      <rect width="1400" height="900" fill="url(#gate-grid)" />
      <circle cx="1080" cy="250" r="420" fill="url(#gate-glow)" />

      {/* Massifs : une seule courbe, emboitee a differentes echelles. */}
      <g className="gate__contours" transform="translate(255 640)">
        {[0.3, 0.46, 0.62, 0.78, 0.94, 1.1].map((scale) => (
          <use key={scale} href="#gate-contour" transform={`scale(${scale})`} />
        ))}
      </g>
      <g className="gate__contours gate__contours--far" transform="translate(1140 690) rotate(-14)">
        {[0.28, 0.48, 0.68, 0.88].map((scale) => (
          <use key={scale} href="#gate-contour" transform={`scale(${scale})`} />
        ))}
      </g>

      {/* La route : ce que l'application sert a dessiner. */}
      <path
        className="gate__route"
        d="M120 760C300 690 360 540 520 470 680 400 760 470 900 400 1030 336 1090 210 1300 150"
        fill="none"
      />
      {[
        [120, 760],
        [520, 470],
        [900, 400],
        [1300, 150],
      ].map(([cx, cy]) => (
        <circle key={`${cx}`} className="gate__waypoint" cx={cx} cy={cy} r="6" />
      ))}
    </svg>
  );
}

export function GateScreen(): ReactNode {
  const { signIn } = useStore();
  const [code, setCode] = useState('');
  const [pseudo, setPseudo] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);

  const trimmedPseudo = pseudo.trim();
  const canSubmit = code.length > 0 && trimmedPseudo.length > 0 && !sending;

  async function submit(formEvent: FormEvent<HTMLFormElement>): Promise<void> {
    formEvent.preventDefault();
    if (!canSubmit) return;
    setSending(true);
    setError(null);
    try {
      await signIn(code, trimmedPseudo);
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 429) {
        setError(`Trop de tentatives. Reessaie dans ${formatRetryDelay(caught.retryAfterSeconds)}.`);
      } else if (caught instanceof ApiError && caught.status === 401) {
        // Message volontairement identique quelle que soit la raison du refus :
        // distinguer « mauvais code » de « pseudo refuse » aiderait a deviner.
        setError("Acces refuse. Verifie le code et reessaie.");
      } else if (caught instanceof ApiError && caught.status === 400) {
        setError('Pseudo invalide : entre 1 et 40 caracteres.');
      } else {
        setError('Le serveur est injoignable. Reessaie dans un instant.');
      }
      setSending(false);
    }
  }

  return (
    <main className="gate">
      <GateBackdrop />
      <div className="gate__grain" aria-hidden="true" />

      <div className="gate__stage">
        <section className="gate__intro">
          <p className="gate__kicker">
            <span className="gate__kicker-rule" aria-hidden="true" />
            Carnet de route
          </p>
          <h1 className="gate__title">
            Asie
            <em>centrale</em>
          </h1>
          <p className="gate__countries">
            Kazakhstan <i aria-hidden="true">/</i> Ouzbekistan <i aria-hidden="true">/</i>{' '}
            Kirghizistan
          </p>
          <p className="gate__lead">
            Une carte, une bande d'amis, un itineraire a construire ensemble. On pose les lieux, on
            vote, on garde les meilleurs.
          </p>
          <ol className="gate__steps">
            <li>
              <span className="gate__step-num">01</span>Poser un lieu sur la carte
            </li>
            <li>
              <span className="gate__step-num">02</span>Voter pour, voter contre
            </li>
            <li>
              <span className="gate__step-num">03</span>Ordonner l'itineraire
            </li>
          </ol>
        </section>

        <form className="gate__card" onSubmit={(formEvent) => void submit(formEvent)}>
          <div className="gate__seal" aria-hidden="true">
            <span>KZ</span>
            <span>UZ</span>
            <span>KG</span>
          </div>

          <h2 className="gate__cardtitle">Acces partage</h2>
          <p className="gate__cardlead">Le code circule dans la conversation du groupe.</p>

          <label className="field">
            <span className="field__label">Code d'acces</span>
            <input
              className="field__input field__input--code"
              type="password"
              autoComplete="off"
              value={code}
              onChange={(inputEvent) => setCode(inputEvent.currentTarget.value)}
              disabled={sending}
            />
          </label>

          <label className="field">
            <span className="field__label">Pseudo</span>
            <input
              className="field__input"
              type="text"
              autoComplete="nickname"
              maxLength={40}
              value={pseudo}
              onChange={(inputEvent) => setPseudo(inputEvent.currentTarget.value)}
              disabled={sending}
            />
          </label>

          {error !== null ? (
            <p className="gate__error" role="alert">
              {error}
            </p>
          ) : null}

          <button className="button button--primary button--block" type="submit" disabled={!canSubmit}>
            {sending ? 'Connexion...' : 'Entrer dans le carnet'}
          </button>

          <p className="gate__note">
            Si le pseudo existe deja, tu reprends l'identite correspondante.
          </p>
        </form>
      </div>
    </main>
  );
}
